import {H1} from '@jetbrains/ring-ui/components/heading/heading'
import {Suspense, useEffect, useMemo} from 'react'
import {useQueryLoader} from 'react-relay'

import {useAppSelector} from '../../../../../hooks/react-redux'
import {useBranch} from '../../../../../hooks/routes'
import {Policy} from '../../../../../rest/branches'
import Routes, {getHref} from '../../../../../routes'
import {getBuildTypeFilter} from '../../../../../types'
import {DEFAULT_NAME} from '../../../../../utils/branchNames'
import {emptyArray} from '../../../../../utils/empty'
import {objectValues} from '../../../../../utils/object'
import type {BranchSelectBranchesQuery} from '../../../../common/BranchSelect/__generated__/BranchSelectBranchesQuery.graphql'
import {
  branchesQuery,
  getBranchesQueryVariables,
} from '../../../../common/BranchSelect/BranchSelect.queries'
import {
  BranchBadge,
  PipelineTimeBadge as TimeBadge,
  SystemProblemsBadge,
  RepositoryBadge,
} from '../../components/Badges'
import PipelineEditButton from '../../components/PipelineEditButton/PipelineEditButton'
import RunButton from '../../components/RunButton/RunButton'
import headerStyles from '../../EditPipelinePage/EditPipelineHeader/EditPipelineHeader.css'
import {getOriginalPipelineAdditionalVcsRoots} from '../../EditPipelinePage/EditPipelinePage.selectors'
import {getRepositoryName} from '../../EditPipelinePage/SettingsSidebarContent/PipelineSettingsSidebar/PipelineRepositories/PipelineRepositories.utils'
import type {pipelineHeadFragment$key} from '../../hooks/__generated__/pipelineHeadFragment.graphql'
import {usePipelineHeadId, usePipelineId} from '../../hooks/pipeline'
import usePipelineDocumentTitle from '../../hooks/usePipelineDocumentTitle'
import {pipelinesApi} from '../../services/pipelinesApi'
import {isNewTimeBadgeEnabled} from '../../utils/featureToggles'

import styles from './PipelineHeader.css'

type Props = {
  pipelineHeadKey: pipelineHeadFragment$key | null
}
export default function PipelineHeader({pipelineHeadKey}: Props) {
  const id = usePipelineId()
  const {name, description, parallelized} = pipelinesApi.endpoints.getPipelineById.useQuery(id, {
    selectFromResult: ({data}) => {
      const {settings, vcsRoot} = data ?? {}
      return {
        name: settings?.name,
        description: getRepositoryName(vcsRoot?.url),
        parallelized: settings?.jobs
          ? objectValues(settings?.jobs).some(({parallelism}) => !!parallelism)
          : null,
      }
    },
  })
  const headId = usePipelineHeadId(pipelineHeadKey)
  const [branchesQueryRef, loadBranches] = useQueryLoader<BranchSelectBranchesQuery>(branchesQuery)
  useEffect(() => {
    if (headId != null) {
      loadBranches(
        getBranchesQueryVariables({
          node: getBuildTypeFilter(headId),
          policy: Policy.ALL_BRANCHES,
        }),
      )
    }
  }, [headId, loadBranches])
  const branch = useBranch()
  const defaultBranch = branch?.default || branch?.groupFlag
  const branchName = defaultBranch ? DEFAULT_NAME : branch?.name

  const pipelineAdditionalVcsRoots =
    useAppSelector(getOriginalPipelineAdditionalVcsRoots(id)) ?? emptyArray
  const additionalVcsRoots = useMemo(
    () => pipelineAdditionalVcsRoots.map(({url}) => url.replace(/^https?:\/\//, '') ?? ''),
    [pipelineAdditionalVcsRoots],
  )

  usePipelineDocumentTitle(name)

  return (
    <div className={styles.header}>
      <div className={styles.heading}>
        <H1 className={headerStyles.title}>{name}</H1>
      </div>
      <div className={styles.badges}>
        {isNewTimeBadgeEnabled ? (
          <TimeBadge branchName={branchName} parallelized={parallelized} />
        ) : null}
        {description != null && (
          <RepositoryBadge displayedRepo={description} repos={additionalVcsRoots} />
        )}
        <BranchBadge headId={headId} branchesQueryRef={branchesQueryRef} />
        <SystemProblemsBadge headId={headId!} />
      </div>
      <div className={styles.actions}>
        <Suspense>
          <PipelineEditButton to={getHref(Routes.EDIT_PIPELINE, {pipelineId: id})} />
        </Suspense>
        <RunButton
          primary
          headId={headId!}
          className={styles.button}
          branchName={branch?.default ? undefined : branch?.name}
        />
      </div>
    </div>
  )
}
